import { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import IssuesActions from './IssuesActions';
import IssueComments from './IssueComments';
import IssueHeader from './IssueHeader';
import IssueInfo from './IssueInfo';
import RatingCard from './rating/RatingCard';
import AttachmentsGallery from './AttachmentsGallery';
import { IssueActionBanner } from './IssueActionBanner';
import Modal from './Modal';
import AppContext from '../contexts/AppContext';
import { Cross2Icon, CheckIcon, ImageIcon, CameraIcon, FileTextIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import DocRenderer from './DocRenderer';
import UploadProgressIndicator from './UploadProgressIndicator';
import { useDeleteIssue } from '../hooks/useDeleteIssue';
import { useDeleteAttachment } from '../hooks/useDeleteAttachment';
import { hasPermission } from '../utils/permissionUtils';
import { getMappedFieldValue } from '../utils/getMappedFieldValue';
import { resolveDetailFields, hasVisibleField } from '../utils/issueDetailFields';
import { isDraftIssue, getDraftSubmitLabel } from '../utils/draftConfig';
import { canReadComments, canWriteComments } from '../utils/commentRights';
import { getErrorMessage } from '../constants/errorCodes';

function IssueDetails() {
  const { t } = useTranslation();
  const { issueKey } = useParams();
  const navigate = useNavigate();
  const { isInWebView, currentUser, serviceDesk } = useContext(AppContext);
  const [issue, setIssue] = useState(null);
  const serviceDeskConfig = issue?.serviceDesk || serviceDesk?.config;
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [forms, setForms] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [quotaCheck, setQuotaCheck] = useState('pending'); // 'pending' | 'ok' | 'blocked' | 'error'
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const rejectCommentIsValid = useMemo(() => rejectComment.length >= 5, [rejectComment]);
  const { mutate: deleteIssue, isPending: isDeleting, isError: isDeleteError, error: deleteError } = useDeleteIssue({
    onSuccess: () => {
      navigate('/issues');
    }
  });

  const { mutate: deleteAttachment, isPending: isDeletingAttachment } = useDeleteAttachment({
    onSuccess: () => {
      setAttachmentToDelete(null);
      fetchAttachments();
    }
  });

  useEffect(() => {
    const fetchIssueDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Toujours récupérer les détails complets (assets, customFieldsLabels, etc.) depuis l'API
        const response = await fetchWithAuth(`/issues/${encodeURIComponent(issueKey)}`);

        // Gestion explicite des erreurs HTTP (401/403/404 notamment)
        if (!response.ok) {
          if ([401, 403, 404].includes(response.status)) {
            setError(t('This ticket cannot be found or you do not have access to it.'));
          } else {
            setError(t('Unable to load the ticket right now. Please try again later.'));
          }
          return;
        }

        const issue = await response.json();

        if (issue.error) {
          setError(issue.details || issue.error);
          return;
        }

        setIssue(issue);
      } catch (err) {
        console.error('Error fetching issue details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (issueKey) {
      fetchIssueDetails();
    }
  }, [issueKey]);

  const fetchAttachments = async () => {
    try {
      setAttachmentsLoading(true);
      const response = await fetchWithAuth(`issues/${encodeURIComponent(issueKey)}/attachments-comments`);
      const data = await response.json();

      if (response.ok && data.success) {
        setAttachments(data.data.attachments || []);
      }
    } catch (err) {
      console.error('Error loading attachments:', err);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  useEffect(() => {
    if (issueKey) {
      fetchAttachments();
    }
  }, [issueKey]);

  const handleDeleteAttachment = (attachment) => {
    setAttachmentToDelete(attachment);
  };

  const handleConfirmDeleteAttachment = () => {
    if (!attachmentToDelete) return;
    deleteAttachment({ issueKey, attachmentId: attachmentToDelete.id });
  };

  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingCount(files.length);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('files', file);

        const response = await fetchWithAuth(
          `issues/${encodeURIComponent(issueKey)}/attachments`,
          { method: 'POST', body: formData }
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          console.error(`Upload failed for ${file.name}:`, data.error);
        }
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
      } finally {
        setUploadingCount(prev => prev - 1);
      }
    }

    await fetchAttachments();

    const cameraInput = document.getElementById('issue-photo-camera');
    const galleryInput = document.getElementById('issue-photo-gallery');
    if (cameraInput) cameraInput.value = '';
    if (galleryInput) galleryInput.value = '';
  };

  const isDraft = isDraftIssue(issue, serviceDeskConfig);

  useEffect(() => {
    if (!isDraft || !issue || !serviceDeskConfig) return;

    const checkDraftQuota = async () => {
      if (!serviceDeskConfig?.hasQuota) {
        setQuotaCheck('ok');
        return;
      }

      const requestTypeId = Number(issue.mappedFields?.requestType?.requestType?.id);

      if (!Number.isFinite(requestTypeId)) {
        setQuotaCheck('error');
        return;
      }

      const fields = Object.fromEntries(
        Object.entries(issue.fields || {}).filter(([k]) => k.startsWith('customfield_'))
      );

      try {
        const response = await fetchWithAuth('/quotas/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceDeskId: serviceDeskConfig.id,
            requestTypeId,
            fields,
          }),
        });

        const data = await response.json();
        // Fail closed: only allow validation on a successful check that is not blocking
        if (!response.ok || !data?.success) setQuotaCheck('error');
        else setQuotaCheck(data.data?.blocking ? 'blocked' : 'ok');
      } catch (err) {
        console.error('Error checking quota for draft:', err);
        setQuotaCheck('error');
      }
    };

    checkDraftQuota();
  }, [isDraft, issue, serviceDeskConfig]);

  // Normaliser l'issue pour l'affichage (clé, statut, priorite, resume, dates...)
  const currentIssue = useMemo(() => {
    if (!issue) return null;
    const { key, fields, mappedFields, names } = issue;

    return {
      key,
      status: fields.status.name,
      statusCategory: fields.statusCategory,
      priority: fields.priority.name,
      summary: mappedFields.summary,
      created: fields.created,
      updated: fields.updated,
      fields,
      names,
      mappedFields,
    };
  }, [issue]);

  const handleFormsUpdated = (forms) => {
    setForms(forms);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800',
    };
    return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      done: 'bg-green-100 text-green-800',
      closed: 'bg-green-100 text-green-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <svg className="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('Error')}</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/issues')}
            className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
          >
            {t('Back to tickets')}
          </button>
        </div>
      </div>
    );
  }

  if (!currentIssue) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('Ticket not found')}</h3>
        <button
          onClick={() => navigate('/issues')}
          className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
        >
          {t('Back to tickets')}
        </button>
      </div>
    );
  }

  const actionsValidated = forms.length > 0 && forms.every(form => form.submitted === true);
  const techForms = forms.filter(form => form.name.toLowerCase().includes('[tech]'));
  const canTechnicianSubmit = techForms.length > 0 && techForms.every(form => form.submitted === true);

  const getTechnicianValidationMessage = () => {
    if (!canTechnicianSubmit) {
      return t('Please validate your intervention form.');
    }

    return t('You can now validate your intervention!');
  };

  const getValidationMessage = () => {
    if (forms.length === 0) {
      return t('Please add an action to perform.');
    }

    if (!forms.every(form => form.submitted === true)) {
      return t('Please validate all actions.');
    }

    return t('Ticket validation');
  };

  const quotaReason = t('Quota reached: this draft cannot be submitted for now.');
  const needsActions = serviceDeskConfig?.hasActions?.includes(currentIssue.mappedFields?.requestType?.requestType?.id);
  // First unmet condition, shown as the reason the submit button is disabled
  const draftBlockReason = quotaCheck === 'blocked'
    ? quotaReason
    : quotaCheck === 'error'
      ? t('Unknown error')
      : (needsActions && !actionsValidated ? getValidationMessage() : null);
  const canSubmitDraft = quotaCheck === 'ok' && !draftBlockReason;
  const canSeeDraftBanner = isDraft && hasPermission(['admin', 'user', 'manager'], currentUser.role);

  const handleTransitionIssue = async (transitionId = null, body = null) => {
    if (!transitionId) {
      console.error('Transition ID not found');
      return false;
    }

    const response = await fetchWithAuth(`/issues/${encodeURIComponent(issueKey)}/transitions/${transitionId}`, {
      method: 'POST',
      ...(body && { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {
      console.error('Error validating issue:', data.error);
      const quotaBlocked = data.errorCode === 'QUOTA_EXCEEDED';
      if (quotaBlocked) setQuotaCheck('blocked');
      const unavailable = data.errorCode === 'INTERVENTION_TRANSITION_UNAVAILABLE';
      setSubmitError(quotaBlocked ? quotaReason : unavailable ? getErrorMessage(t, data.errorCode) : (data.error || t('Unknown error')));
      return false;
    }

    navigate('/issues');
    return true;
  }

  const submitDraft = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await handleTransitionIssue(serviceDeskConfig?.draft?.submitTransitionId);
    } catch (err) {
      console.error('Error submitting draft:', err);
      setSubmitError(t('Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitIntervention = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdInterventionSuccess?.[0]);
    } catch (err) {
      console.error('Error validating intervention:', err);
      setSubmitError(t('Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIssue = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteIssue(issueKey);
    setIsDeleteModalOpen(false);
  };

  const handleRejectIssue = () => {
    setRejectComment('');
    setSubmitError(null);
    setIsRejectModalOpen(true);
  };

  const handleArchiveIssue = () => {
    setIsArchiveModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    try {
      await handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdArchive);
      setIsArchiveModalOpen(false);
    } catch (err) {
      console.error('Error archiving issue:', err);
      setError(err.message);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectComment.trim()) {
      return;
    }

    try {
      // The reason is sent with the reject transition, which posts it as a comment
      const userDisplayName = currentUser?.name || 'Utilisateur';
      const commentTextWithAuthor = `[${userDisplayName}] ${rejectComment.trim()}`;

      const rejected = await handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdReject, { comment: commentTextWithAuthor });
      // On failure keep the modal open with the typed reason, the error shows inside it
      if (!rejected) return;

      setIsRejectModalOpen(false);
      setRejectComment('');
    } catch (err) {
      console.error('Error rejecting issue:', err);
      setSubmitError(t('Unknown error'));
    }
  };

  const canValidateIssue = () => {
    if (!currentIssue) return false;
    if (currentIssue.status === 'OUVERT') return true;
    if (currentIssue.status === 'En attente de validation DRV') return hasPermission(['admin', 'manager'], currentUser.role);
    return canSeeDraftBanner;
  };

  const showValidationBanner = canValidateIssue();

  const assetsKeys = ['sector', 'shopSign', 'intervention_point', 'brand', 'furniture'];
  // Static labels WE authored for the qualification block. Each key maps to a
  // dedicated literal translation call, never a dynamic lookup, so the i18n
  // static guard stays green.
  const getAssetLabel = (key) => {
    switch (key) {
      case 'sector':
        return t('Sector');
      case 'shopSign':
        return t('Banner');
      case 'intervention_point':
        return t('Store');
      case 'brand':
        return t('Brand');
      case 'furniture':
        return t('Furniture');
      default:
        return key;
    }
  };
  const showActions = true;

  const allowedDetailKeys = [
    'description',
    'problems',
    'assets',
    'duedate',
    'intervention_time',
    'delivery_date',
    'reception_date',
    'carrier_tracking',
    'contact_name',
    'contact_email',
    'contact_phone',
    'loading_dock',
    'delivery_time_slot',
    'desired_intervention_date',
    'desired_intervention_time'
  ];

  const fieldsMapping = serviceDeskConfig?.fieldsMapping || {};
  const allowedKeySet = new Set(allowedDetailKeys);

  // Named blocks defined per service desk, rendered at the top in order.
  // Section keys are constrained to the allowlist so only sanctioned fields can be shown.
  const detailsSections = (serviceDeskConfig?.detailsSections || [])
    .map(section => ({ title: section.title, keys: (section.keys || []).filter(key => allowedKeySet.has(key)) }));
  const sectionedKeys = new Set(detailsSections.flatMap(section => section.keys));

  // Catch-all "Détails de la demande": every allowed key not assigned to a named block.
  const catchAllFields = resolveDetailFields(
    allowedDetailKeys.filter(key => !sectionedKeys.has(key)),
    currentIssue,
    fieldsMapping
  );

  // Resolve each named block and drop those whose fields are all unset.
  const resolvedSections = detailsSections
    .map((section, index) => ({ id: `section-${index}`, title: section.title, fields: resolveDetailFields(section.keys, currentIssue, fieldsMapping) }))
    .filter(section => hasVisibleField(section.fields));

  // Localize the per-field empty placeholder. The label flows through config as a
  // stable English key, so we resolve it with a literal-keyed switch (never a
  // dynamic translation lookup) to keep the i18n static guard green.
  const resolveDisplayValue = (field) => {
    if (!field.isEmpty || field.displayValue == null) {
      return field.displayValue;
    }

    switch (field.displayValue) {
      case 'Not specified':
        return t('Not specified');
      default:
        return field.displayValue;
    }
  };

  // Build the <tr> rows for a list of resolved fields.
  const renderFieldRows = (fields) => fields
    .filter(field => field.isADFDoc || field.displayValue != null)
    .map((field, index) => (
      <tr key={`${field.key}-${index}`} className="align-top">
        <th className="w-1/3 px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left bg-gray-50">
          {field.label}
        </th>
        <td className="px-4 py-3 text-sm text-gray-900">
          {field.isADFDoc ? (
            <DocRenderer doc={field.rawValue} className="text-sm text-gray-900" />
          ) : (
            resolveDisplayValue(field) || '-'
          )}
        </td>
      </tr>
    ));

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className={`container mx-auto py-8 ${!isInWebView && showValidationBanner ? 'pb-24' : 'pb-4'}`}>
          <IssueHeader
            issue={currentIssue}
            statusColor={currentIssue.statusCategory?.colorName || getStatusColor(currentIssue.status)}
            onBack={() => navigate('/issues')}
            onDelete={isDraft ? handleDeleteIssue : null}
            isDeleting={isDeleting}
            isDeleteError={isDeleteError}
            deleteError={deleteError}
          />

          <div className="grid grid-cols-1 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <IssueInfo issue={currentIssue} getPriorityPill={getPriorityColor} />

              {/* Only the reporter is asked to rate; the backend 403 stays the real guard. */}
              {currentUser?.accountId && currentUser.accountId === currentIssue.fields?.reporter?.accountId && (
                <RatingCard issueKey={issueKey} />
              )}

              {resolvedSections.map((section) => (
                <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {section.title}
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="min-w-full text-sm">
                        <tbody className="divide-y divide-gray-200">
                          {renderFieldRows(section.fields)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}

              {showActions && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-6">
                    <IssuesActions issue={issue} currentIssue={currentIssue} currentUser={currentUser} onFormsUpdated={handleFormsUpdated} />
                  </div>
                </div>
              )}

{assetsKeys.some(key => currentIssue.mappedFields[key]) && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" strokeWidth={2} />
                        <circle cx="12" cy="12" r="6" strokeWidth={2} />
                        <circle cx="12" cy="12" r="2" strokeWidth={2} />
                      </svg>
                      {t('Qualification details')}
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="min-w-full text-sm">
                        <tbody className="divide-y divide-gray-200">
                          {assetsKeys
                            .filter(key => currentIssue.mappedFields[key])
                            .map((key) => {
                              const value = currentIssue.mappedFields[key];

                              return (
                                <tr key={key} className="align-top">
                                  <th className="w-1/3 px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left bg-gray-50">
                                    {getAssetLabel(key)}
                                  </th>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {Array.isArray(value) && value.length > 0 ? (
                                      <ul className="space-y-1">
                                        {value.map((asset, assetIndex) => (
                                          <li key={assetIndex} className="font-medium">
                                            {asset?.value || t('Item {{number}}', { number: assetIndex + 1 })}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      getMappedFieldValue(value) || '-'
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {hasVisibleField(catchAllFields) && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileTextIcon className="w-5 h-5" />
                      {t('Request details')}
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="min-w-full text-sm">
                        <tbody className="divide-y divide-gray-200">
                          {renderFieldRows(catchAllFields)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6">

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  {t('Attachments')}
                  {attachments.length > 0 && (
                    <span className="bg-black text-white rounded-full px-2 py-0.5 text-xs font-medium">
                      {attachments.length}
                    </span>
                  )}
                </h3>
              </div>
              <div className="p-6">
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label
                    htmlFor="issue-photo-camera"
                    className={`cursor-pointer inline-flex items-center px-5 py-3 border-2 border-gray-300
                      rounded-lg text-sm font-medium text-gray-700 bg-white
                      hover:bg-gray-50 hover:border-gray-400 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black
                      ${uploadingCount > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <CameraIcon className="w-5 h-5 mr-2 text-gray-600" />
                    {t('Take a photo')}
                  </label>
                  <input
                    type="file"
                    id="issue-photo-camera"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={uploadingCount > 0}
                    onChange={(e) => handlePhotoUpload(Array.from(e.target.files || []))}
                  />

                  <label
                    htmlFor="issue-photo-gallery"
                    className={`cursor-pointer inline-flex items-center px-5 py-3 border-2 border-gray-300
                      rounded-lg text-sm font-medium text-gray-700 bg-white
                      hover:bg-gray-50 hover:border-gray-400 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black
                      ${uploadingCount > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <ImageIcon className="w-5 h-5 mr-2 text-gray-600" />
                    {t('Add from gallery')}
                  </label>
                  <input
                    type="file"
                    id="issue-photo-gallery"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={uploadingCount > 0}
                    onChange={(e) => handlePhotoUpload(Array.from(e.target.files || []))}
                  />
                </div>

                <div className="mt-4">
                  <AttachmentsGallery
                    attachments={attachments}
                    loading={attachmentsLoading}
                    onImageClick={setSelectedImage}
                    editable
                    onDelete={handleDeleteAttachment}
                  />
                </div>
                <div className="mt-3">
                  <UploadProgressIndicator uploadCount={uploadingCount} />
                </div>
              </div>
            </div>

            {canReadComments(issue?.serviceDesk, currentUser?.role) && (
              <IssueComments
                issueKey={issueKey}
                currentUser={currentUser}
                onImageClick={setSelectedImage}
                canWrite={canWriteComments(issue?.serviceDesk, currentUser?.role)}
              />
            )}

          </div>
        </div>
        {canSeeDraftBanner && (
          <IssueActionBanner
            title={submitError ?? draftBlockReason ?? (needsActions ? getValidationMessage() : t('Your request is a draft: it will not be processed until you submit it.'))}
            icon={canSubmitDraft ? <CheckIcon className="w-5 h-5 text-green-600" /> : null}
            buttonText={getDraftSubmitLabel(t, serviceDeskConfig?.draft)}
            buttonIcon={<CheckIcon className="w-4 h-4" />}
            onClick={submitDraft}
            disabled={!canSubmitDraft || submitting}
            isInWebView={isInWebView}
          />
        )}
        {hasPermission(['admin', 'manager'], currentUser.role) && Number(currentIssue.fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdValidation && (
          <IssueActionBanner
            title={getValidationMessage()}
            icon={<CheckIcon className="w-5 h-5 text-green-600" />}
            buttons={[
              {
                text: t('Reject for changes'),
                icon: <Cross2Icon className="w-4 h-4" />,
                onClick: handleRejectIssue,
                variant: 'secondary'
              },
              {
                text: t('Decline and close'),
                icon: <Cross2Icon className="w-4 h-4" />,
                onClick: handleArchiveIssue,
                variant: 'secondary'
              },
              {
                text: t('Validate'),
                icon: <CheckIcon className="w-4 h-4" />,
                onClick: () => handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdValidation),
                variant: 'primary'
              }
            ]}
            isInWebView={isInWebView}
          />
        )}
        {hasPermission(['technician'], currentUser.role) && Number(currentIssue.fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdIntervention && (
          <IssueActionBanner
            title={submitError ? <span role="alert" className="text-red-600">{submitError}</span> : getTechnicianValidationMessage()}
            icon={submitError ? <ExclamationTriangleIcon className="w-5 h-5 text-red-600" /> : canTechnicianSubmit ? <CheckIcon className="w-5 h-5 text-green-600" /> : null}
            buttonText={t('Validate')}
            onClick={submitIntervention}
            disabled={!canTechnicianSubmit || submitting}
            isInWebView={isInWebView}
          />
        )}
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-full max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors flex items-center gap-2 bg-black/50 px-4 py-2 rounded-lg text-sm"
            >
              <Cross2Icon className="w-5 h-5" />
              {t('Close')}
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.filename}
              className="w-full max-h-96 object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Modal of deletion confirmation */}
      <Modal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onApply={handleConfirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        title={t('Delete ticket')}
        description={t('Are you sure you want to delete ticket {{key}}? This action cannot be undone.', { key: currentIssue?.key })}
        cancelLabel={t('Cancel')}
        applyLabel={isDeleting ? t('Deleting…') : t('Delete')}
        trigger={null}
        showActions={true}
      />

      {/* Modal of rejection confirmation */}
      <Modal
        open={isRejectModalOpen}
        onOpenChange={setIsRejectModalOpen}
        onApply={handleConfirmReject}
        onCancel={() => {
          setIsRejectModalOpen(false);
          setRejectComment('');
        }}
        title={t('Reject ticket')}
        description={t('Please explain the reason for rejecting ticket {{key}}.', { key: currentIssue?.key })}
        cancelLabel={t('Cancel')}
        applyLabel={t('Reject')}
        trigger={null}
        showActions={true}
        disableApply={!rejectCommentIsValid}
      >
        <div className="mb-4">
          <label htmlFor="rejectComment" className="block text-sm font-medium text-gray-700 mb-2">
            {t('Rejection comment')} <span className="text-red-600">*</span>
          </label>
          <textarea
            id="rejectComment"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder={t('Explain why this ticket is rejected…')}
            className="w-full min-h-[120px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent resize-vertical"
            required
          />
          {!rejectCommentIsValid && (
            <p className="mt-1 text-sm text-gray-500">{t('This field is required')}</p>
          )}
          {submitError && <p className="mt-1 text-sm text-red-600">{submitError}</p>}
        </div>
      </Modal>

      {/* Modal of attachment deletion confirmation */}
      <Modal
        open={!!attachmentToDelete}
        onOpenChange={(open) => { if (!open) setAttachmentToDelete(null); }}
        onApply={handleConfirmDeleteAttachment}
        onCancel={() => setAttachmentToDelete(null)}
        title={t('Delete attachment')}
        description={t('Are you sure you want to delete "{{filename}}"?', { filename: attachmentToDelete?.filename })}
        cancelLabel={t('Cancel')}
        applyLabel={isDeletingAttachment ? t('Deleting…') : t('Delete')}
        trigger={null}
        showActions={true}
        disableApply={isDeletingAttachment}
      />

      {/* Modal of archive confirmation */}
      <Modal
        open={isArchiveModalOpen}
        onOpenChange={setIsArchiveModalOpen}
        onApply={handleConfirmArchive}
        onCancel={() => setIsArchiveModalOpen(false)}
        title={t('Decline and close the ticket')}
        cancelLabel={t('Cancel')}
        applyLabel={t('Decline and close')}
        trigger={null}
        showActions={true}
      >
        <div className="text-sm text-gray-600 space-y-3">
          <p>{t('You are about to decline and close ticket')} <span className="font-semibold text-gray-900">{currentIssue?.key}</span>.</p>
          <p>{t('The ticket will remain viewable in read-only mode in the archived tickets tab, but can no longer be edited.')}</p>
          <p>{t('If this ticket needs to be reactivated later, a new request must be created by the ECM.')}</p>
          <p className="font-medium text-gray-800">{t('Do you want to confirm this action?')}</p>
        </div>
      </Modal>

    </div>
  );
}

export default IssueDetails;
